/* eslint-disable no-extra-parens */
/* eslint-disable indent */
/* eslint-disable max-lines-per-function */
import {DEFAULT_RETURN_DATA} from 'extensions/rma/data/constants';
import 'extensions/ui-components/x-message';
import 'extensions/ui-components/x-quantity';
import {imageFallback} from 'extensions/ui-directives';
import {defineCustomElement, formatPrice} from 'extensions/ui-utils';
import {rangeArray} from 'extensions/ui-utils/lib/range-array';
import {LitElement, html, nothing} from 'lit';
import {map} from 'lit/directives/map.js';
import {range} from 'lit/directives/range.js';
import './x-rma-product-return-data';
import stylesheet from './x-rma-product.scss?as=stylesheet';

export class RMAProduct extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			category: {
				type: String
			},
			disabled: {
				type: Boolean
			},
			imageUrl: {
				attribute: 'image-url',
				type: String
			},
			lineId: {
				attribute: 'line-id',
				type: Number
			},
			price: {
				type: Number
			},
			quantity: {
				type: Number
			},
			reasons: {
				type: Array
			},
			returnData: {
				type: Array
			},
			selected: {
				type: Boolean
			},
			serialNumbers: {
				attribute: 'serial-numbers',
				type: Array
			},
			sku: {
				type: String
			},
			tax: {
				type: Number
			},
			totalQuantity: {
				attribute: 'total-quantity',
				type: Number
			}
		};
	}

	get subTotal() {
		return this.price * this.quantity;
	}

	get taxTotal () {
		return this.tax * this.quantity;
	}

	get total() {
		return this.subTotal + this.taxTotal;
	}

	/**
	 * @type {import('extensions/rma/types').RMAProductState}
	 */
	get state () {
		return {
			lineId: this.lineId,
			quantity: this.quantity,
			returnData: this.returnData,
			selected: this.selected,
			sku: this.sku,
			subTotal: this.subTotal,
			tax: this.taxTotal,
			total: this.total,
			valid: Boolean(this.selected && this.quantity >= 1) && this.returnData?.every(returnData => returnData.valid)
		};
	}

	constructor () {
		super();

		/**
		 * @type {string}
		 */
		this.category = '';

		/**
		 * @type {boolean}
		 */
		this.disabled = false;

		/**
		 * @type {string}
		 */
		this.imageUrl = '';

		/**
		 * @type {number}
		 */
		this.lineId = 0;

		/**
		 * @type {number}
		 */
		this.price = 0;

		/**
		 * @type {number}
		 */
		this.quantity = 1;

		/**
		 * @type {import('extensions/rma/types').Reason[]}
		 */
		this.reasons = [];

		/**
		 * @type {import('extensions/rma/types').RMAReturnData[]}
		 */
		this.returnData = [];

		/**
		 * @type {boolean}
		 */
		this.selected = false;

		/**
		 * @type {string[]}
		 */
		this.serialNumbers = [];

		/**
		 * @type {string}
		 */
		this.sku = '';

		/**
		 * @type {number}
		 */
		this.tax = 0;

		/**
		 * @type {number}
		 */
		this.totalQuantity = 1;
	}

	dispatchStateChange () {
		this.dispatchEvent(new CustomEvent('statechange', {
			bubbles: true,
			composed: true,
			detail: this.state
		}));
	}

	/**
	 *
	 * @returns {import('extensions/rma/types').RMAReturnData}
	 */
	updateReturnDataAfterQuantityChange () {
		return rangeArray(this.quantity)
			.map(i => this.returnData?.[i] ?? DEFAULT_RETURN_DATA);
	}

	async handleCheckboxChange () {
		this.selected = !this.selected;

		this.dispatchStateChange();

		// Scroll into view when checking the box
		if (this.selected) {
			await this.updateComplete;
			this.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			});
		}
	}

	handleQuantityChange (e) {
		const [target] = e.composedPath();

		this.quantity = target.value;

		// Update return data model after quantity changes (add/remove returnData objects)
		this.returnData = this.updateReturnDataAfterQuantityChange();

		this.dispatchStateChange();
	}

	handleReturnDataChange (e) {

		/**
		 * @type {import('extensions/rma/types').RMAReturnData}
		 */
		const returnData = e.detail;

		this.returnData = this.returnData ?? [];
		this.returnData[returnData.index] = returnData;

		this.dispatchStateChange();
	}

	renderReturnData () {
		if (this.selected) {
			return map(range(this.quantity), i => {
				const returnData = this.returnData?.[i];

				// Get array of already selected serial numbers and filter out the current instance's serial number value

				/**
				 * Used to filter out the serial number array to prevent duplicate selections
				 */
				const selectedSerialNumbers = this.returnData
					?.map(_returnData => _returnData.serialNumber)
					?.filter(serialNumber => serialNumber !== returnData.serialNumber);

				return html`
					<x-rma-return-data
						@return-data:statechange="${this.handleReturnDataChange}"
						.reasons="${this.reasons}"
						.serialNumbers="${
							selectedSerialNumbers?.length > 0
								? this.serialNumbers.filter(serialNumber => !selectedSerialNumbers?.includes(serialNumber))
								: this.serialNumbers
						}"

						?show-serial-numbers="${this.serialNumbers.length > 0}"
						?show-index-label="${this.quantity > 1}"
						index="${i}"
						sku="${this.sku}"
						serial-number="${returnData?.serialNumber}"
						reason="${returnData?.reason}"
						item-condition="${returnData?.itemCondition}"
						.uploadData="${returnData?.uploadData}"
					></x-rma-return-data>
				`;
			});
		}

		return nothing;
	}

	render () {
		return html`
			<div class="container">
				<div class="product-row">
					<div class="product-column-checkbox">
						<label class="c-form-checkbox c-form-checkbox--inline checkbox-only">
							<input ?disabled="${this.disabled}" class="c-form-checkbox__input" id="l-checkbox" type="checkbox" @change="${this.handleCheckboxChange}" ?checked="${this.selected}" />
							<span class="c-form-checkbox__caption"></span>
						</label>
					</div>
					<div class="product-column-info">
						<label for="l-checkbox" class="product-info">
							<div class="product-image">
								<img src="${this.imageUrl}" alt="${this.sku} image" loading="lazy" ${imageFallback(window.mivaJS.Image_Not_Found_Grid)} />
							</div>
							<div class="product-name">
								<div class="product-category">${this.category}</div>
								<div class="product-sku">${this.sku}</div>
							</div>
						</label>

						${this.disabled ? html`<x-message class="not-eligible-message" type="basic">This item is not eligable for return</x-message>` : nothing}
					</div>

					<div class="product-column-quantity">
						<label class="form-label hide--large">Qty</label>
						${
							this.disabled
								? nothing
								: html`<div class="quantity-container">
									<x-quantity hide-buttons-mobile hide-buttons-tablet max="${this.totalQuantity}" value="${this.quantity}" @change="${this.handleQuantityChange}"></x-quantity>
									<span class="quantity-total">(of ${this.totalQuantity})</span>
								</div>`
						}
					</div>

					<div class="product-column-price">
						<div class="form-label hide--large hide-visually">&nbsp;</div>
						<div class="form-label hide--large">Subtotal</div>
						<div class="price-group">
							<div class="product-total">${formatPrice(this.subTotal)}</div>
							<div class="product-price">${formatPrice(this.price)} ea</div>
						</div>
					</div>
				</div>
				${this.renderReturnData()}
			</div>
		`;
	}
};

defineCustomElement('x-rma-product', RMAProduct);
